/* A tooltip trigger has to be focusable to be reachable by keyboard, and this one is a status
   glyph rather than a control, so no interactive role applies to it. */
/* eslint-disable jsx-a11y-x/no-noninteractive-tabindex */
import { RadioTower } from 'lucide-react';
import { useEffect, useState } from 'react';

import { useIntl } from '../../hooks';
import { resolveActiveSuppressionEndMs } from '../../lib/active-suppression-end';
import { formatTime } from '../../lib/date-time';
import { formatDuration } from '../../lib/format-duration';
import { hzToMhz } from '../../lib/frequency';
import { cn } from '../../lib/utils';
import type { ActiveSuppressionRecord } from '../../types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui';

const TOOLTIP_DELAY_MS = 300;
const COUNTDOWN_TICK_INTERVAL_MS = 1_000;

type ActiveSuppressionIndicatorProps = {
  records: ActiveSuppressionRecord[];
  isOnline: boolean;
  animated: boolean;
};

export const ActiveSuppressionIndicator = ({
  records,
  isOnline,
  animated,
}: ActiveSuppressionIndicatorProps) => {
  const intl = useIntl();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  // The countdowns only need to advance while a human is reading them.
  useEffect(() => {
    if (!isTooltipOpen) {
      return;
    }

    setNowMs(Date.now());
    const countdownTicker = setInterval(() => setNowMs(Date.now()), COUNTDOWN_TICK_INTERVAL_MS);

    return () => clearInterval(countdownTicker);
  }, [isTooltipOpen]);

  if (records.length === 0) {
    return null;
  }

  const label = intl.formatMessage(
    { id: 'deviceCard.activeSuppressions.label' },
    { count: records.length },
  );

  const formatFrequency = (record: ActiveSuppressionRecord): string | null => {
    if (record.channelFrequencyList && record.channelFrequencyList.length > 0) {
      return intl.formatMessage(
        { id: 'deviceCard.activeSuppressions.frequencyList' },
        { count: record.channelFrequencyList.length },
      );
    }

    if (record.channelFrequencyStart !== null && record.channelFrequencyStop !== null) {
      return intl.formatMessage(
        { id: 'deviceCard.activeSuppressions.frequencyRange' },
        {
          start: hzToMhz(record.channelFrequencyStart),
          stop: hzToMhz(record.channelFrequencyStop),
        },
      );
    }

    if (record.channelFrequency !== null) {
      return intl.formatMessage(
        { id: 'deviceCard.activeSuppressions.frequency' },
        { value: hzToMhz(record.channelFrequency) },
      );
    }

    return null;
  };

  const formatStartedAt = (record: ActiveSuppressionRecord): string | null => {
    const startedAt = formatTime(record.startedAt);
    return startedAt === null
      ? null
      : intl.formatMessage({ id: 'deviceCard.activeSuppressions.startedAt' }, { time: startedAt });
  };

  const describeRecord = (record: ActiveSuppressionRecord): string =>
    [
      intl.formatMessage({ id: `deviceCard.activeSuppressions.kind.${record.kind}` }),
      formatFrequency(record),
      formatStartedAt(record),
      intl.formatMessage(
        { id: 'deviceCard.activeSuppressions.remaining' },
        { remaining: formatDuration(resolveActiveSuppressionEndMs(record) - nowMs) },
      ),
    ]
      .filter((part): part is string => part !== null)
      .join(' · ');

  const orderedRecords = [...records].sort(
    (first, second) => Date.parse(first.startedAt) - Date.parse(second.startedAt),
  );

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
        {/* The attributes live on the outer span: Radix owns data-state on the trigger itself. */}
        <span
          data-test-id="active-suppression-indicator"
          data-indicator-state={isOnline ? 'active' : 'offline'}
          data-count={records.length}
          className={cn(
            'inline-flex h-8 cursor-help items-center pr-2',
            isOnline ? 'text-destructive' : 'text-destructive-dark',
          )}
        >
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              role="img"
              aria-label={label}
              className={cn(
                'relative inline-flex',
                animated && 'motion-safe:animate-suppression-pulse',
              )}
            >
              <RadioTower className="h-4 w-4" />
              {/* A plain span, never a Badge: a badge class here shifts the card's badge indexes. */}
              {records.length > 1 && (
                <span className="absolute -top-1.5 -right-2 text-[10px] leading-none font-semibold tabular-nums">
                  {records.length}
                </span>
              )}
            </span>
          </TooltipTrigger>
        </span>
        <TooltipContent side="right" className="max-w-xs">
          <div data-test-id="active-suppression-lines">
            {!isOnline && (
              <p>{intl.formatMessage({ id: 'deviceCard.activeSuppressions.offlinePrefix' })}</p>
            )}
            {orderedRecords.map((record) => (
              <p key={record.id}>{describeRecord(record)}</p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
